import { Col, Input, Modal, Row, Upload, Select, message, Spin } from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import styles from "./modals.module.scss";
import SecondaryButton from "../components/Button/secondaryButton";
import Button from "../components/Button";
import { useSecureService } from "../store/service";
import { ApiFilePostRequest, ApiFilePostStatusEnum } from "../service/apis/FileApi";
import { UserApi, Configuration } from '../service';
import type {  UploadProps } from 'antd/es/upload/interface';
import { ReactComponent as ImagePlaceholder } from "../images/image-placeholder.svg";
import { useLanguage } from '../contexts/LanguageContext';

interface AddNewFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId?: number; // use parentId instead of folderId
  projectId?: number;
  onSuccess?: (newVersionData?: { id: number; name: string; path: string; shouldNavigateToLatest: boolean }) => void; // shouldNavigateToLatest flag'i ekledik
  isFromFileDetail?: boolean;
  fileId?: number; // newly added
  editMode?: boolean; // Edit mode için
  editData?: any; // Edit edilecek veri
  fileDetail?: any; // File detail bilgisi
}

const userApi = new UserApi(new Configuration());

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 300MB in bytes

const AddNewFileModal = ({ isOpen, onClose, parentId, projectId, onSuccess, isFromFileDetail, fileId, editMode = false, editData, fileDetail }: AddNewFileModalProps) => {
  const { t } = useLanguage();
  const [thumbnail, setThumbnail] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  // Status options'ı dil dosyasından oluştur
  const statusOptions = Object.entries(ApiFilePostStatusEnum).map(([key, value]) => ({
    label: t(`projects.statusOptions.${key.toLowerCase()}`),
    value: value
  }));
  const [share, setShare] = useState<string[]>([]);
  const [shareOptions, setShareOptions] = useState<{label: string, value: string}[]>([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [status, setStatus] = useState<ApiFilePostStatusEnum>(ApiFilePostStatusEnum.NeedsReview);
  const [isSaving, setIsSaving] = useState(false);
  const uploadRef = useRef<HTMLInputElement>(null);
  const { createFile, editFile } = useSecureService();

  // Edit mode'da form'u doldur
  useEffect(() => {
    if (editMode && editData) {
      setTitle(editData.name || '');
      
      // Share verisini email'lere dönüştür
      if (editData.share && Array.isArray(editData.share)) {
        const emails = editData.share.map((item: any) => item.email || item).filter((email: any) => typeof email === 'string' && email.trim());
        setShare(emails);
      } else {
        setShare([]);
      }
      
      // Thumbnail ve file edit mode'da değiştirilemez
    } else if (isFromFileDetail && !editMode && fileDetail) {
      // Versiyon eklerken sadece file title'ı dolu getir
      setTitle(fileDetail.name || '');
    } else if (isFromFileDetail && !editMode && editData) {
      // Versiyon eklerken sadece file title'ı dolu getir
      setTitle(editData.name || '');
    }
  }, [editMode, editData, isOpen, isFromFileDetail, fileDetail]);

  const handleImageClick = () => {
    if (uploadRef.current) {
      uploadRef.current.click();
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setThumbnail(e.target.files[0]);
    }
  };

  const handleShareSearch = async (value: string) => {
    if (!value) {
      setShareOptions([]);
      return;
    }
    setShareLoading(true);
    try {
      const response = await userApi.apiUserEmailsGet({ searchTerm: value }, { credentials: 'include' });
      if (response.success && response.data && response.data.emails) {
        setShareOptions(
          response.data.emails
            .filter((email: string) => email && typeof email === 'string' && email.trim())
            .map((email: string) => ({
              label: email,
              value: email
            }))
        );
      } else {
        setShareOptions([]);
      }
    } catch {
      setShareOptions([]);
    } finally {
      setShareLoading(false);
    }
  };

  const handleClose = () => {
    setTitle("");
    setFile(null);
    setShare([]);
    setStatus(ApiFilePostStatusEnum.NeedsReview);
    setThumbnail(null);
    onClose();
  };

  // Modal kapandığında form'u temizle
  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setFile(null);
      setShare([]);
      setStatus(ApiFilePostStatusEnum.NeedsReview);
      setThumbnail(null);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!title) {
      message.error(t("projects.errors.createFileError"));
      return;
    }

    if (!editMode && !file) {
      message.error(t("projects.fileUpload.selectFile"));
      return;
    }

    if (file && file.size > MAX_FILE_SIZE) {
      message.error(t("projects.fileUpload.fileSizeExceeded"));
      return;
    }

    setIsSaving(true);
    try {
      if (editMode && editData) {
        // Edit mode - sadece id, name, thumbnail ve share gönder
        const editDataToSend: any = {
          id: editData.id,
          name: title,
          share,
        };
        
        // Sadece yeni thumbnail varsa ekle
        if (thumbnail) {
          editDataToSend.thumbnail = thumbnail;
        }
        
        const result = await editFile(editDataToSend);
        
        if (result && result.success) {
          handleClose();
          onSuccess?.();
        } else {
          message.error(t("projects.fileUpload.fileUpdated"));
        }
      } else {
        // Create mode
        const fileData: ApiFilePostRequest = {
          name: title,
          file: file!,
          folderId: isFromFileDetail ? undefined : parentId,
          projectId,
          status,
          share,
          thumbnail,
          ...(isFromFileDetail && fileId ? { fileId } : {})
        };

        const result = await createFile(fileData);

        if (result && result.success) {
          handleClose();
          // Yeni versiyon yüklendikten sonra en son versiyona navigate et
          const newVersionData = result.data ? {
            id: result.data.id,
            name: result.data.name || title,
            path: result.data.path || result.data.name || title,
            shouldNavigateToLatest: true // Navigate flag'i ekle
          } : undefined;
          onSuccess?.(newVersionData);
        } else {
          message.error(t("projects.fileUpload.fileCreated"));
        }
      }
    } catch (error) {
      message.error(editMode ? t("projects.fileUpload.updateError") : t("projects.fileUpload.uploadError"));
    } finally {
      setIsSaving(false);
    }
  };



  const dragProps: UploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload: (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        message.error(t("projects.fileUpload.fileSizeExceeded"));
        return false;
      }
      setFile(file);
      return false;
    },
    onRemove: () => {
      setFile(null);
    },
    fileList: file ? [{ uid: '-1', name: file.name, status: 'done' as const }] : [],
    showUploadList: true
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      title={<div className={styles.modalTitle}>{editMode ? t("common.edit") + " " + t("projects.newFile") : t("projects.newFile")}</div>}
      className={styles.modal}
      closable
      closeIcon={<CloseOutlined style={{ color: "var(--app-accent-color)" }} />}
      width={686}
    >
      <Spin spinning={isSaving}>
        <Row className={styles.modalRow} gutter={[24, 24]}>
        <Col xs={24} md={10}>
          <div className={styles.leftSide}>
            <div
              className={styles.thumbnail}
              onClick={handleImageClick}
            >
              {thumbnail ? (
                <img
                  src={URL.createObjectURL(thumbnail)}
                  alt="thumbnail"
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
              ) : (
                <div style={{ width: 117, height: 94, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ImagePlaceholder />
                </div>
              )}
              <input
                ref={uploadRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleThumbnailChange}
              />
            </div>
            <div className={styles.projectTitle} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>{t("projects.fileName")}</label>
              <Input placeholder={t("projects.fileName")} value={title} onChange={e => setTitle(e.target.value)} />
            </div>
          </div>
        </Col>
        <Col xs={24} md={14}>
          {!editMode && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>{t("projects.status")}</label>
              <Select
                style={{ width: '100%' }}
                placeholder={t("common.select") + " " + t("projects.status").toLowerCase()}
                options={statusOptions}
                value={status}
                onChange={value => setStatus(value as ApiFilePostStatusEnum)}
              />
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 8 }}>{t("projects.share")}</label>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              placeholder="Add email(s)"
              value={share}
              onChange={setShare}
              onSearch={handleShareSearch}
              options={shareOptions}
              filterOption={false}
              notFoundContent={
                <div style={{ color: '#fff', background: '#222', padding: 8, textAlign: 'center', borderRadius: 8 }}>
                  {shareLoading ? t("common.loading") : "No email found"}
                </div>
              }
              showSearch
              tagRender={() => <span style={{ display: 'none' }} />}
            />
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {share.filter(email => typeof email === 'string' && email.trim()).map(email => (
                <div
                  key={email}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#363B3F',
                    borderRadius: 20,
                    padding: '4px 12px',
                    color: '#fff',
                    fontWeight: 500
                  }}
                >
                  <span style={{
                    background: 'var(--app-accent-secondary)',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 8,
                    fontWeight: 700,
                    fontSize: 16
                  }}>
                    {email && email[0] ? email[0].toUpperCase() : '?'}
                  </span>
                  {email}
                  <span
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                    onClick={() => setShare(share.filter(e => e !== email))}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          </div>
          {!editMode && (
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>{t("projects.fileUpload.uploadFile")}</label>
              <Upload.Dragger {...dragProps} style={{ background: '#23272A', border: '1px dashed var(--app-accent-color)', borderRadius: 8 }}>
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ color: 'var(--app-accent-color)', fontSize: 32 }} />
                </p>
                <p className="ant-upload-text">{t("projects.fileUpload.dragAndDrop")}</p>
                <p className="ant-upload-hint">{t("projects.fileUpload.singleFile")}</p>
              </Upload.Dragger>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <SecondaryButton onClick={handleClose}>{t("common.cancel")}</SecondaryButton>
              <Button onClick={handleSave} className={styles.saveButton} style={{ marginRight: 8 }} disabled={isSaving}>
                {isSaving ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? t("common.edit") : t("common.save"))}
              </Button>
          </div>
        </Col>
      </Row>
      </Spin>
    </Modal>
  );
};

export default AddNewFileModal; 